import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Badge from '@/components/base/Badge';
import { downloadCSV } from '@/lib/csv';
import { todayMexico } from '@/lib/dates';
import Modal from '@/components/base/Modal';

interface Cliente {
  id: number;
  company_name: string;
  rfc: string;
  address: string;
  credit_status: string;
  credit_limit: number;
  current_balance: number;
  status: string;
  requires_invoice: boolean;
}

export default function Clientes() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEstatus, setFilterEstatus] = useState('Todos');
  const [filterCredito, setFilterCredito] = useState('Todos');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Cliente | null>(null);
  const [newClient, setNewClient] = useState({
    company_name: '',
    rfc: '',
    calle: '',
    ciudad: '',
    estado: '',
    cp: '',
    credit_status: 'none' as 'none' | '30days',
    credit_limit: '0',
    requires_invoice: true,
  });
  const [clientError, setClientError] = useState('');
  const [clientSuccess, setClientSuccess] = useState('');

  useEffect(() => {
    fetchClients();
  }, []);

  async function fetchClients() {
    try {
      const { data, error } = await supabase
        .from('erp_clients')
        .select('*')
        .order('company_name');
      if (error) throw error;
      setClients(data || []);
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  }

  const handleExportCSV = () => {
    if (paginated.length === 0) return;
    const headers = ['ID', 'Razón Social', 'RFC', 'Dirección', 'Factura', 'Crédito', 'Saldo Pendiente', 'Estatus'];
    const rows = paginated.map(c => [
      `CLI-${String(c.id).padStart(3, '0')}`,
      c.company_name,
      c.rfc || '',
      c.address || '',
      c.requires_invoice ? 'Requiere Factura' : 'Sin Factura',
      c.credit_status === '30days' ? 'Crédito 30 Días' : 'Sin Crédito',
      String(c.current_balance),
      c.status === 'active' ? 'Activo' : c.status === 'delinquent' ? 'Moroso' : 'Inactivo',
    ]);
    downloadCSV(headers, rows, `clientes-${todayMexico()}.csv`);
  };

  const filtered = (() => {
    return clients.filter((c) => {
      const matchSearch = c.company_name.toLowerCase().includes(search.toLowerCase())
        || (c.rfc || '').toLowerCase().includes(search.toLowerCase())
        || String(c.id).includes(search.toLowerCase());
      const matchEstatus = filterEstatus === 'Todos'
        || (filterEstatus === 'Activo' && c.status === 'active')
        || (filterEstatus === 'Inactivo' && c.status === 'inactive')
        || (filterEstatus === 'Moroso' && c.status === 'delinquent');
      const matchCredito = filterCredito === 'Todos'
        || (filterCredito === 'Crédito 30 Días' && c.credit_status === '30days')
        || (filterCredito === 'Sin Crédito' && c.credit_status === 'none');
      return matchSearch && matchEstatus && matchCredito;
    });
  })();

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 6 }).format(val);

  const statusLabel = (s: string) => {
    if (s === 'active') return 'Activo';
    if (s === 'delinquent') return 'Moroso';
    return 'Inactivo';
  };

  const statusVariant = (s: string) => {
    if (s === 'active') return 'success';
    if (s === 'delinquent') return 'danger';
    return 'neutral';
  };

  const creditLabel = (c: string) => c === '30days' ? 'Crédito 30 Días' : 'Sin Crédito';

  const extractCity = (addr: string) => {
    const parts = addr.split(',');
    return parts.length >= 2 ? parts[parts.length - 2].trim() : addr;
  };

  function parseAddress(addr: string | null) {
    if (!addr) return { calle: '', ciudad: '', estado: '', cp: '' };
    const parts = addr.split(',').map(p => p.trim());
    const cpMatch = parts.find(p => p.toUpperCase().startsWith('C.P.'));
    const cp = cpMatch ? cpMatch.replace(/C\.P\.\s*/i, '').trim() : '';
    const filtered = parts.filter(p => !p.toUpperCase().startsWith('C.P.'));
    const calle = filtered[0] || '';
    const ciudad = filtered.length >= 2 ? filtered[filtered.length - 2] : '';
    const estado = filtered.length >= 1 ? filtered[filtered.length - 1] : '';
    return { calle, ciudad, estado, cp };
  }

  function resetNewClientForm() {
    setNewClient({
      company_name: '',
      rfc: '',
      calle: '',
      ciudad: '',
      estado: '',
      cp: '',
      credit_status: 'none',
      credit_limit: '0',
      requires_invoice: true,
    });
    setClientError('');
    setClientSuccess('');
  }

  function openEditModal(cliente: Cliente) {
    setEditingClient(cliente);
    const addr = parseAddress(cliente.address);
    setNewClient({
      company_name: cliente.company_name,
      rfc: cliente.rfc || '',
      calle: addr.calle,
      ciudad: addr.ciudad,
      estado: addr.estado,
      cp: addr.cp,
      credit_status: (cliente.credit_status as 'none' | '30days') || 'none',
      credit_limit: String(cliente.credit_limit || 0),
      requires_invoice: cliente.requires_invoice ?? true,
    });
    setClientError('');
    setClientSuccess('');
    setShowEditClientModal(true);
  }

  async function handleCreateClient() {
    setClientError('');
    setClientSuccess('');

    if (!newClient.company_name.trim() && !newClient.rfc.trim()) {
      setClientError('Debe ingresar al menos la Razón Social o el RFC para identificar al cliente'); return;
    }

    if (newClient.requires_invoice) {
      if (!newClient.rfc.trim()) { setClientError('El RFC es obligatorio cuando el cliente requiere factura'); return; }
      if (!newClient.calle.trim() || !newClient.ciudad.trim() || !newClient.estado.trim()) {
        setClientError('La dirección fiscal (calle, ciudad, estado) es obligatoria cuando el cliente requiere factura'); return;
      }
    }

    const fullAddress = newClient.calle.trim()
      ? `${newClient.calle.trim()}, ${newClient.ciudad.trim()}, ${newClient.estado.trim()}${newClient.cp.trim() ? ', C.P. ' + newClient.cp.trim() : ''}`
      : '';
    const limit = parseFloat(newClient.credit_limit) || 0;

    try {
      const { error } = await supabase
        .from('erp_clients')
        .insert({
          company_name: newClient.company_name.trim() || 'Cliente',
          rfc: newClient.rfc.trim().toUpperCase() || null,
          address: fullAddress || null,
          credit_status: newClient.credit_status,
          credit_limit: limit,
          current_balance: 0,
          status: 'active',
          requires_invoice: newClient.requires_invoice,
        });

      if (error) throw error;

      setClientSuccess('Cliente creado exitosamente');
      await fetchClients();

      setTimeout(() => {
        setShowNewClientModal(false);
        resetNewClientForm();
      }, 800);

    } catch (err: any) {
      setClientError(err?.message || 'Error al crear el cliente. Intente de nuevo.');
    }
  }

  async function handleEditClient() {
    setClientError('');
    setClientSuccess('');

    if (!editingClient) return;
    if (!newClient.company_name.trim() && !newClient.rfc.trim()) {
      setClientError('Debe ingresar al menos la Razón Social o el RFC'); return;
    }

    if (newClient.requires_invoice) {
      if (!newClient.rfc.trim()) { setClientError('El RFC es obligatorio cuando el cliente requiere factura'); return; }
      if (!newClient.calle.trim() || !newClient.ciudad.trim() || !newClient.estado.trim()) {
        setClientError('La dirección fiscal es obligatoria cuando el cliente requiere factura'); return;
      }
    }

    const fullAddress = newClient.calle.trim()
      ? `${newClient.calle.trim()}, ${newClient.ciudad.trim()}, ${newClient.estado.trim()}${newClient.cp.trim() ? ', C.P. ' + newClient.cp.trim() : ''}`
      : '';
    const limit = parseFloat(newClient.credit_limit) || 0;

    try {
      const { error } = await supabase
        .from('erp_clients')
        .update({
          company_name: newClient.company_name.trim() || 'Cliente',
          rfc: newClient.rfc.trim().toUpperCase() || null,
          address: fullAddress || null,
          credit_status: newClient.credit_status,
          credit_limit: limit,
          requires_invoice: newClient.requires_invoice,
        })
        .eq('id', editingClient.id);

      if (error) throw error;

      setClientSuccess('Cliente actualizado exitosamente');
      await fetchClients();

      setTimeout(() => {
        setShowEditClientModal(false);
        setEditingClient(null);
        resetNewClientForm();
      }, 800);

    } catch (err: any) {
      setClientError(err?.message || 'Error al actualizar el cliente. Intente de nuevo.');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-foreground-500">Cargando clientes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground-900">Clientes</h1>
          <p className="text-sm text-foreground-500 mt-0.5">{clients.length} clientes registrados</p>
        </div>
        <button
          onClick={() => { resetNewClientForm(); setShowNewClientModal(true); }}
          className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 cursor-pointer transition-colors whitespace-nowrap self-start"
        >
          <i className="ri-add-line"></i>
          Nuevo Cliente
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por Razón Social, RFC o ID..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 placeholder:text-foreground-400"
          />
        </div>
        <select
          value={filterEstatus}
          onChange={(e) => { setFilterEstatus(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
        >
          <option value="Todos">Todos los estados</option>
          <option value="Activo">Activo</option>
          <option value="Inactivo">Inactivo</option>
          <option value="Moroso">Moroso</option>
        </select>
        <select
          value={filterCredito}
          onChange={(e) => { setFilterCredito(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
        >
          <option value="Todos">Todos los créditos</option>
          <option value="Crédito 30 Días">Crédito 30 Días</option>
          <option value="Sin Crédito">Sin Crédito</option>
        </select>
        <button
          onClick={handleExportCSV}
          className="flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium text-foreground-600 bg-white border border-background-200 rounded-lg hover:bg-background-50 cursor-pointer transition-colors whitespace-nowrap"
        >
          <i className="ri-download-2-line"></i>
          Exportar CSV
        </button>
      </div>

      <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-background-100 bg-background-50">
                <th className="text-left px-5 py-3 font-semibold text-foreground-500">ID</th>
                <th className="text-left px-5 py-3 font-semibold text-foreground-500">Razón Social</th>
                <th className="text-left px-5 py-3 font-semibold text-foreground-500">RFC</th>
                <th className="text-left px-5 py-3 font-semibold text-foreground-500">Ciudad</th>
                <th className="text-center px-5 py-3 font-semibold text-foreground-500">Factura</th>
                <th className="text-center px-5 py-3 font-semibold text-foreground-500">Crédito</th>
                <th className="text-right px-5 py-3 font-semibold text-foreground-500">Saldo Pendiente</th>
                <th className="text-center px-5 py-3 font-semibold text-foreground-500">Estatus</th>
                <th className="text-center px-5 py-3 font-semibold text-foreground-500"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {paginated.map((c) => (
                <tr key={c.id} className="hover:bg-background-50 transition-colors">
                  <td className="px-5 py-3.5 cursor-pointer" onClick={() => navigate(`/clientes/${c.id}`)}>
                    <span className="font-mono text-xs font-semibold text-primary-600">CLI-{String(c.id).padStart(3, '0')}</span>
                  </td>
                  <td className="px-5 py-3.5 cursor-pointer" onClick={() => navigate(`/clientes/${c.id}`)}>
                    <span className="font-medium text-foreground-800">{c.company_name}</span>
                  </td>
                  <td className="px-5 py-3.5 cursor-pointer" onClick={() => navigate(`/clientes/${c.id}`)}>
                    <span className="font-mono text-foreground-600">{c.rfc || '—'}</span>
                  </td>
                  <td className="px-5 py-3.5 cursor-pointer text-foreground-600" onClick={() => navigate(`/clientes/${c.id}`)}>{extractCity(c.address) || '—'}</td>
                  <td className="px-5 py-3.5 text-center cursor-pointer" onClick={() => navigate(`/clientes/${c.id}`)}>
                    {c.requires_invoice ? (
                      <Badge variant="info">Factura</Badge>
                    ) : (
                      <Badge variant="neutral">Sin factura</Badge>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-center cursor-pointer" onClick={() => navigate(`/clientes/${c.id}`)}>
                    <Badge variant={c.credit_status === '30days' ? 'info' : 'neutral'}>
                      {creditLabel(c.credit_status)}
                    </Badge>
                  </td>
                  <td className="px-5 py-3.5 text-right cursor-pointer" onClick={() => navigate(`/clientes/${c.id}`)}>
                    <span className={`font-semibold ${Number(c.current_balance) > Number(c.credit_limit) * 0.8 && Number(c.credit_limit) > 0 ? 'text-red-600' : 'text-foreground-800'}`}>
                      {formatCurrency(Number(c.current_balance))}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-center cursor-pointer" onClick={() => navigate(`/clientes/${c.id}`)}>
                    <Badge variant={statusVariant(c.status)}>{statusLabel(c.status)}</Badge>
                  </td>
                  <td className="px-5 py-3.5 text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => openEditModal(c)}
                      className="w-7 h-7 rounded-md bg-amber-50 hover:bg-amber-100 flex items-center justify-center cursor-pointer transition-colors"
                      title="Editar cliente"
                    >
                      <i className="ri-edit-line text-amber-600 text-xs"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="py-12 text-center">
            <i className="ri-search-line text-foreground-300 text-3xl"></i>
            <p className="text-sm text-foreground-500 mt-2">No se encontraron clientes</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-foreground-500">
            Mostrando {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, filtered.length)} de {filtered.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="w-8 h-8 rounded-lg border border-background-200 flex items-center justify-center cursor-pointer hover:bg-background-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <i className="ri-arrow-left-s-line text-foreground-600 text-sm"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`w-8 h-8 rounded-lg text-xs font-medium cursor-pointer transition-colors ${
                  p === currentPage
                    ? 'bg-primary-600 text-white'
                    : 'border border-background-200 text-foreground-600 hover:bg-background-50'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="w-8 h-8 rounded-lg border border-background-200 flex items-center justify-center cursor-pointer hover:bg-background-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <i className="ri-arrow-right-s-line text-foreground-600 text-sm"></i>
            </button>
          </div>
        </div>
      )}

      <Modal open={showNewClientModal} onClose={() => { setShowNewClientModal(false); resetNewClientForm(); }} title="Nuevo Cliente" size="lg">
        {renderClientForm(false)}
      </Modal>

      <Modal open={showEditClientModal} onClose={() => { setShowEditClientModal(false); setEditingClient(null); resetNewClientForm(); }} title="Editar Cliente" size="lg">
        {renderClientForm(true)}
      </Modal>
    </div>
  );

  function renderClientForm(isEdit: boolean) {
    return (
      <div className="space-y-4">
        {clientSuccess && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
            <i className="ri-checkbox-circle-fill text-emerald-500"></i>
            <p className="text-xs text-emerald-700 font-medium">{clientSuccess}</p>
          </div>
        )}
        {clientError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
            <i className="ri-error-warning-fill text-red-500"></i>
            <p className="text-xs text-red-700 font-medium">{clientError}</p>
          </div>
        )}

        <div className="bg-background-50 rounded-lg p-4 flex items-center justify-between">
          <div>
            <span className="text-sm font-semibold text-foreground-800">Requiere Factura</span>
            <p className="text-xs text-foreground-500 mt-0.5">Actívelo si el cliente necesita factura fiscal con RFC y dirección</p>
          </div>
          <button
            onClick={() => setNewClient({ ...newClient, requires_invoice: !newClient.requires_invoice })}
            className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${newClient.requires_invoice ? 'bg-emerald-500' : 'bg-background-300'}`}
          >
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform shadow-sm ${newClient.requires_invoice ? 'translate-x-6' : 'translate-x-0.5'}`}></span>
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1">
              Razón Social {newClient.requires_invoice && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={newClient.company_name}
              onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
              placeholder="Ej. Transportes del Norte S.A. de C.V."
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1">
              RFC {newClient.requires_invoice && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={newClient.rfc}
              onChange={(e) => setNewClient({ ...newClient, rfc: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 font-mono"
              placeholder={newClient.requires_invoice ? 'Ej. TNO230512ABC' : 'Opcional sin factura'}
              maxLength={13}
            />
          </div>
        </div>

        {newClient.requires_invoice && (
          <div>
            <span className="block text-xs font-semibold text-foreground-700 mb-2">Dirección Fiscal <span className="text-red-500">*</span></span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <input
                  type="text"
                  value={newClient.calle}
                  onChange={(e) => setNewClient({ ...newClient, calle: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                  placeholder="Calle y número"
                />
              </div>
              <div>
                <input
                  type="text"
                  value={newClient.ciudad}
                  onChange={(e) => setNewClient({ ...newClient, ciudad: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                  placeholder="Ciudad"
                />
              </div>
              <div>
                <input
                  type="text"
                  value={newClient.estado}
                  onChange={(e) => setNewClient({ ...newClient, estado: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                  placeholder="Estado"
                />
              </div>
              <div>
                <input
                  type="text"
                  value={newClient.cp}
                  onChange={(e) => setNewClient({ ...newClient, cp: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                  placeholder="C.P. (opcional)"
                />
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1">Tipo de Crédito</label>
            <select
              value={newClient.credit_status}
              onChange={(e) => setNewClient({ ...newClient, credit_status: e.target.value as 'none' | '30days' })}
              className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
            >
              <option value="none">Sin Crédito</option>
              <option value="30days">Crédito 30 Días</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1">Límite de Crédito (MXN)</label>
            <input
              type="number"
              value={newClient.credit_limit}
              onChange={(e) => setNewClient({ ...newClient, credit_limit: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
              placeholder="0"
              min="0"
              step="1000"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={() => {
              if (isEdit) { setShowEditClientModal(false); setEditingClient(null); }
              else { setShowNewClientModal(false); }
              resetNewClientForm();
            }}
            className="px-4 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={isEdit ? handleEditClient : handleCreateClient}
            className="px-4 py-2 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-save-line"></i>
            {isEdit ? 'Actualizar Cliente' : 'Guardar Cliente'}
          </button>
        </div>
      </div>
    );
  }
}